import { useLocation, useNavigate, useParams } from "react-router-dom";
import ClientDetails from "./ClientInfo/ClientDetails";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, Divider, MenuItem, Modal, TextField, Typography, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LicensedFamilies from "./ClientInfo/LicensedFamilies";
import StatusByFamily from "./ClientInfo/StatusByFamily";
import ParalegalInstructions from "./ClientInfo/ParalegalInstructions";
import PpsInstructions from "./ClientInfo/PpsInstructions";
import RecurringActions from "./ClientInfo/RecurringActions";
import ForeignAssociates from "./ClientInfo/ForeignAssociates";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect, useRef, useState } from "react";
import Inventors from "./ClientInfo/Inventors";
import InventorValidationHistory from "./InventorBulkUpload/InventorValidationHistory";
import Billing from "./ClientInfo/Billing";
import ClientPreferences from "./ClientInfo/ClientPreferences";
import AttorneyPreferences from "./ClientInfo/AttorneyPreferences";
import Annuties from "./ClientInfo/Annuties";
import AdditionalInfo from "./ClientInfo/AdditionalInfo";
import WorkingTeam from "./ClientInfo/WorkingTeam";
import { axios_tenant } from "../../../axios";
import transformClientResponse from "./utils/transformClientResponse";
import { LockOutlined, ToggleOffOutlined, ToggleOnOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import TruncatedText from "./components/TruncatedText";
import { emitKotFormDirty } from "./utils/kotDirtyEvents";
import { downloadBlobResponse } from "./utils/downloadHelpers";
import { resolveSectionHeader } from "./utils/sectionHeader";

const slugToModel = {
  "client-details": "ClientProfile",
  "licensed-families": "LicensedFamily",
  "status-by-family": "ShadowingStatusFamily",
  "paralegal-instructions": "ParalegalInstruction",
  "pps-instructions": "PpsInstruction",
  "recurring-actions": "RecurringAction",
  "foreign-associates": "ForeignAssociate",
  inventors: "Inventor",
  // Read-only view of the Inventor section's upload history, so it inherits that
  // section's permissions: anyone who can see inventors can see how they got there.
  "inventor-validation-history": "Inventor",
  billing: "BillingInvoicing",
  "client-preferences": "ClientPreference",
  "attorney-preferences": "AgentAttorneyPreference",
  annuties: "Annuity",
  "additional-info": "AdditionalInformation",
  "working-team": "ProfileAssignment",
};

// These are profile assignment roles for Private Profiles (not KoT RBAC roles)
const assignmentRoles = ["PPS", "FFS", "US Patent Paralegal", "Patent Renewal Specialist", "Patent Ops", "Practitioner"];

const DOCX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

const AddClient = () => {
  const location = useLocation();
  const section = location.pathname.split("/").pop();
  const [loading, setLoading] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [myAccess, setMyAccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("assign");
  const [users, setUsers] = useState([]);

  const [selectedValues, setSelectedValues] = useState({});
  const [roleData, setRoleData] = useState({});
  const [primaryUserIds, setPrimaryUserIds] = useState({});

  const [userRolesMap, setUserRolesMap] = useState({});
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const hasUnsavedChangesRef = useRef(false);
  const lastAllowedPathRef = useRef(location.pathname);

  const [openConfirm, setOpenConfirm] = useState(false);
  const dispatch = useDispatch();
  const { clientId } = useParams();
  const navigate = useNavigate();

  const handleOpenConfirm = () => setOpenConfirm(true);
  const handleCloseConfirm = () => setOpenConfirm(false);

  const handleConfirmAction = () => {
    handleToggleActive(); // actual action
    setOpenConfirm(false);
  };

  const markUnsavedChanges = useCallback(() => {
    if (!hasUnsavedChangesRef.current) {
      hasUnsavedChangesRef.current = true;
      emitKotFormDirty();
      window.__kotHasUnsavedChanges = true;
    }
  }, []);

  const clearUnsavedChanges = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    window.__kotHasUnsavedChanges = false;
  }, []);

  useEffect(
    () => () => {
      window.__kotHasUnsavedChanges = false;
    },
    [],
  );

  useEffect(() => {
    const handleGlobalRouteClick = (event) => {
      if (!hasUnsavedChangesRef.current) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('[data-ignore-dirty="true"]')) return;

      const linkElement = target.closest("a[href]");
      if (!linkElement) return;

      const targetPath = linkElement.getAttribute("href");
      if (!targetPath) return;

      event.preventDefault();
      event.stopPropagation();
      window.dispatchEvent(new CustomEvent("kot-nav-request", { detail: { to: targetPath } }));
    };

    const handleRemoteDirty = () => markUnsavedChanges();
    const handleRemoteSave = () => clearUnsavedChanges();
    const handleRemoteReset = () => clearUnsavedChanges();

    document.addEventListener("click", handleGlobalRouteClick, true);
    window.addEventListener("kot-form-dirty", handleRemoteDirty);
    window.addEventListener("kot-form-saved", handleRemoteSave);
    window.addEventListener("kot-form-reset", handleRemoteReset);

    return () => {
      document.removeEventListener("click", handleGlobalRouteClick, true);
      window.removeEventListener("kot-form-dirty", handleRemoteDirty);
      window.removeEventListener("kot-form-saved", handleRemoteSave);
      window.removeEventListener("kot-form-reset", handleRemoteReset);
    };
  }, [markUnsavedChanges, clearUnsavedChanges, location.pathname]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (!hasUnsavedChangesRef.current) return;
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  useEffect(() => {
    const handleOpenAssignmentModal = (event) => {
      handleModelOpen(event.detail);
    };

    const handleKotNavigationRequest = (event) => {
      const targetPath = event?.detail?.to;
      if (!targetPath) {
        return;
      }

      if (!hasUnsavedChangesRef.current) {
        navigate(targetPath);
        return;
      }

      setPendingNavigation({ to: targetPath, options: {} });
      setShowLeaveDialog(true);
    };

    document.addEventListener("open-assignment-modal", handleOpenAssignmentModal);
    window.addEventListener("kot-nav-request", handleKotNavigationRequest);

    return () => {
      document.removeEventListener("open-assignment-modal", handleOpenAssignmentModal);
      window.removeEventListener("kot-nav-request", handleKotNavigationRequest);
    };
  }, [navigate]);

  const fetchUsers = async () => {
    try {
      const [res, rolesRes] = await Promise.all([axios_tenant.get("/user/fetchKotUsers"), axios_tenant.get("/kot/admin/users/roles")]);

      const users = res.data.data.userResults || [];

      setUsers(users);
      setUserRolesMap(rolesRes.data || {});
    } catch (e) {
      console.error(e);
    }
  };

  const isUserEligibleForRole = (userId, assignmentRole) => {
    const roles = userRolesMap[userId] || [];
    //Enable to Allow Admin to be assigned to a client
    // if (roles.includes("ADMIN")) return true;

    // Attempt fuzzy matching between assignment role and KOT admin roles
    const searchStr = assignmentRole.toUpperCase().replace(/\s+/g, "_");
    return roles.some((r) => {
      const dbRole = r.toUpperCase();
      return dbRole === searchStr || dbRole.includes(searchStr) || searchStr.includes(dbRole) || (searchStr.includes("PARALEGAL") && dbRole.includes("PARALEGAL"));
    });
  };

  const handleSelectChange = (role, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [role]: value,
    }));
    markUnsavedChanges();
  };

  const handleAdd = (role) => {
    const userId = selectedValues[role];
    if (!userId) return;

    const selectedUser = users.find((u) => u.personid === userId);
    if (!selectedUser) return;

    setRoleData((prev) => {
      const existing = prev[role] || [];

      // prevent duplicates
      if (existing.some((u) => u.personid === userId)) return prev;

      return {
        ...prev,
        [role]: [...existing, selectedUser],
      };
    });
    markUnsavedChanges();
  };

  const handleDelete = (role, userId) => {
    setRoleData((prev) => ({
      ...prev,
      [role]: prev[role]?.filter((u) => u.personid !== userId),
    }));
    if (primaryUserIds[role] === userId) {
      setPrimaryUserIds((prev) => ({ ...prev, [role]: null }));
    }
    markUnsavedChanges();
  };

  const handleSetPrimary = (role, userId) => {
    setPrimaryUserIds((prev) => ({
      ...prev,
      [role]: prev[role] === userId ? null : userId,
    }));
    markUnsavedChanges();
  };

  const attemptNavigation = useCallback(
    (to, options = {}) => {
      if (!hasUnsavedChangesRef.current) {
        navigate(to, options);
        return;
      }

      setPendingNavigation({ to, options });
      setShowLeaveDialog(true);
    },
    [navigate],
  );

  const attemptProtectedAction = useCallback((action) => {
    if (!hasUnsavedChangesRef.current) {
      action();
      return;
    }

    setPendingAction(() => action);
    setShowLeaveDialog(true);
  }, []);

  useEffect(() => {
    if (!hasUnsavedChangesRef.current) {
      lastAllowedPathRef.current = location.pathname;
      return;
    }

    if (location.pathname !== lastAllowedPathRef.current) {
      setPendingNavigation({
        to: location.pathname,
        options: { replace: true, state: location.state },
      });
      setShowLeaveDialog(true);
      navigate(lastAllowedPathRef.current, { replace: true });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    const fetchMyAccess = async () => {
      try {
        setAccessLoading(true);
        const res = await axios_tenant.get("/kot/me/access");
        setMyAccess(res.data);
      } catch (e) {
        console.error("Failed to fetch KoT access:", e);
      } finally {
        setAccessLoading(false);
      }
    };

    fetchMyAccess();
  }, []);

  useEffect(() => {
    if (!location.state?.saveSuccessMessage && !location.state?.saveErrorMessage) {
      return;
    }

    if (location.state?.saveSuccessMessage) {
      toast.success(location.state.saveSuccessMessage);
    }

    if (location.state?.saveErrorMessage) {
      toast.error(location.state.saveErrorMessage);
    }

    window.history.replaceState({}, document.title, `${location.pathname}${location.search}`);
  }, [location.pathname, location.search, location.state]);

  useEffect(() => {
    clearUnsavedChanges();
  }, [clientId, clearUnsavedChanges]);

  useEffect(() => {
    const fetchClient = async () => {
      if (clientId && clientId !== "new") {
        try {
          setLoading(true);

          dispatch({
            type: "RESET_CLIENT_FORM",
          });

          dispatch({
            type: "SET_CLIENT_MODE",
            payload: {
              mode: "edit",
              clientId,
            },
          });

          const response = await axios_tenant.get(`/kot/client-profiles/${clientId}`);
          const data = response.data;
          console.log(data);
          const transformedData = transformClientResponse(data);
          dispatch({
            type: "SET_FULL_CLIENT",
            payload: transformedData,
          });

          if (transformedData.assignmentsByRole) {
            setRoleData(transformedData.assignmentsByRole);
            const initialPrimaries = {};
            Object.keys(transformedData.assignmentsByRole).forEach((role) => {
              const primaryUser = transformedData.assignmentsByRole[role].find((u) => u.isPrimary);
              if (primaryUser) initialPrimaries[role] = primaryUser.personid;
            });
            setPrimaryUserIds(initialPrimaries);
          }
        } catch (error) {
          console.error("Failed to fetch client:", error);
        } finally {
          setLoading(false);
        }
      } else {
        dispatch({
          type: "SET_CLIENT_MODE",
          payload: {
            mode: "create",
            clientId: null,
          },
        });
      }
    };

    const handleRefresh = () => {
      fetchClient();
    };

    globalThis.addEventListener("refresh-client-profile", handleRefresh);

    fetchClient();

    return () => {
      globalThis.removeEventListener("refresh-client-profile", handleRefresh);
    };
  }, [clientId, dispatch]);

  const mode = clientId ? "edit" : "create";

  const fullClientData = useSelector((state) => state.kotClient);
  const clientDetails = fullClientData?.clientDetails || {};
  const isPrivateProfile = fullClientData?.isPrivate || false;
  const isInactive = fullClientData?.status === "INACTIVE";
  const sectionPermissionsById = fullClientData?.sectionPermissionsById || {};

  const sectionHeader = resolveSectionHeader(section, clientId);
  const sectionModel = slugToModel[section] || null;
  const permsFromProfile = sectionModel ? sectionPermissionsById[sectionModel] : null;
  const permsFromAccess = sectionModel ? myAccess?.permissionsByModel?.[sectionModel] : null;
  const perms = permsFromProfile || permsFromAccess;

  let access = "none";
  if (perms?.canView) access = perms?.canEdit ? "edit" : "view";

  if (isInactive) {
    access = "view";
  }

  useEffect(() => {
    fetchUsers();
  }, []);

  const renderStep = () => {
    if (access === "none") {
      return <Typography>You do not have access to this section</Typography>;
    }

    const props = { mode: access };

    if (section === "client-details") return <ClientDetails {...props} users={users} userRolesMap={userRolesMap} roleData={roleData} setRoleData={setRoleData} setPrimaryUserIds={setPrimaryUserIds} />;
    if (section === "licensed-families") return <LicensedFamilies {...props} />;
    if (section === "status-by-family") return <StatusByFamily {...props} />;
    if (section === "paralegal-instructions") return <ParalegalInstructions {...props} />;
    if (section === "pps-instructions") return <PpsInstructions {...props} />;
    if (section === "recurring-actions") return <RecurringActions {...props} />;
    if (section === "foreign-associates") return <ForeignAssociates {...props} />;
    if (section === "inventors") return <Inventors {...props} />;
    if (section === "inventor-validation-history") return <InventorValidationHistory {...props} />;
    if (section === "billing") return <Billing {...props} />;
    if (section === "client-preferences") return <ClientPreferences {...props} />;
    if (section === "attorney-preferences") return <AttorneyPreferences {...props} />;
    if (section === "annuties") return <Annuties {...props} />;
    if (section === "additional-info") return <AdditionalInfo {...props} users={users} userRolesMap={userRolesMap} />;
    if (section === "working-team") return <WorkingTeam {...props} />;

    return null;
  };

  if (loading || accessLoading) {
    return (
      <Box p={4} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  const safeClientName = clientDetails?.clientName || "";

  const canShowOption = location.pathname === `/kot/client/edit/${clientId}/client-details`;
  const isTenantAdmin = (localStorage.getItem("TENANT_ROLE") || "").toLowerCase() === "admin";

  const handleToggleActive = async () => {
    if (!isTenantAdmin) {
      return;
    }

    try {
      if (isInactive) {
        await axios_tenant.post(`/kot/client-profiles/${clientId}/activate`);
      } else {
        await axios_tenant.post(`/kot/client-profiles/${clientId}/deactivate`);
      }
      clearUnsavedChanges();
      window.location.reload();
    } catch (error) {
      console.error(error);
    }
  };

  const handlePrivate = async () => {
    if (!isTenantAdmin) {
      return;
    }

    try {
      setPrivacyLoading(true);
      // 1. Update Assignments
      const assignments = [];
      Object.entries(roleData).forEach(([role, userList]) => {
        userList.forEach((user) => {
          assignments.push({
            userId: user.personid,
            role: role,
            isPrimary: primaryUserIds[role] === user.personid,
          });
        });
      });

      await axios_tenant.put(`/kot/client-profiles/${clientId}/assignments`, { assignments });

      // 2. Update Privacy (toggle if it was changed, or just set based on UI)
      // For now, if they click Save in the modal, we likely want to set IS_PRIVATE to true if assignments exist,
      // or maybe there's a specific toggle. In the UI, the button shows the state.
      // Let's just set it to the current UI state or true if assignments exist.
      const isPrivate = assignments.length > 0;
      await axios_tenant.put(`/kot/client-profiles/${clientId}/privacy`, { isPrivate });

      handleModalClose();

      globalThis.dispatchEvent(new Event("refresh-client-profile"));
      clearUnsavedChanges();
      dispatch({
        type: "UPDATE_SECTION",
        payload: { section: "isPrivate", value: isPrivate },
      });
    } catch (error) {
      console.error("Failed to update privacy/assignments:", error);
    } finally {
      setPrivacyLoading(false);
    }
  };

  async function downloadClientDocx() {
    try {
      const res = await axios_tenant.get(`/kot/client-profiles/${clientId}/export?format=docx`, {
        responseType: "blob",
      });

      await downloadBlobResponse(res, `Client_Profile_${clientId}.docx`, DOCX_CONTENT_TYPE);
    } catch (error) {
      console.error("DOCX download failed:", error);
      if (!error?.response) {
        toast.error(error?.message || "Failed to download client document.");
      }
    }
  }

  const navigateTrackChanges = () => {
    attemptNavigation(`/kot/track-changes/${clientId}`, {
      state: {
        clientName: safeClientName,
        clientNumber: clientDetails?.clientNumber,
      },
    });
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setModalMode("assign");
  };

  const confirmLeave = () => {
    if (pendingNavigation) {
      lastAllowedPathRef.current = pendingNavigation.to;
      clearUnsavedChanges();
      navigate(pendingNavigation.to, pendingNavigation.options);
    }

    if (pendingAction) {
      pendingAction();
    }

    setPendingNavigation(null);
    setPendingAction(null);
    setShowLeaveDialog(false);
  };

  const cancelLeave = () => {
    setPendingNavigation(null);
    setPendingAction(null);
    setShowLeaveDialog(false);

    if (location.pathname !== lastAllowedPathRef.current) {
      navigate(lastAllowedPathRef.current, { replace: true });
    }
  };

  const handleModelOpen = async (options = {}) => {
    if (!isTenantAdmin) {
      return;
    }

    await fetchUsers();
    setModalMode(options?.mode === "assign" ? "assign" : isPrivateProfile ? "confirm-public" : "assign");
    setModalOpen(true);
  };

  const handleMakePublic = async () => {
    try {
      setPrivacyLoading(true);

      // Make profile public
      await axios_tenant.put(`/kot/client-profiles/${clientId}/privacy`, {
        isPrivate: false,
      });

      const rolesToPreserve = ["PPS", "FFS", "US Patent Paralegal"];

      const preservedAssignments = [];

      Object.entries(roleData).forEach(([role, userList]) => {
        userList.forEach((user) => {
          const isPrimary = primaryUserIds[role] === user.personid;

          const shouldPreserve = rolesToPreserve.includes(role) && isPrimary;

          if (shouldPreserve) {
            preservedAssignments.push({
              userId: user.personid,
              role,
              isPrimary: true,
            });
          }
        });
      });

      await axios_tenant.put(`/kot/client-profiles/${clientId}/assignments`, {
        assignments: preservedAssignments,
      });

      const preservedUserIdsByRole = preservedAssignments.reduce((roleMap, assignment) => {
        roleMap[assignment.role] = assignment.userId;
        return roleMap;
      }, {});

      setRoleData((currentRoleData) =>
        Object.entries(currentRoleData).reduce((nextRoleData, [role, userList]) => {
          const primaryUserId = preservedUserIdsByRole[role];
          if (primaryUserId) {
            nextRoleData[role] = userList.filter((user) => user.personid === primaryUserId);
          }
          return nextRoleData;
        }, {}),
      );
      setPrimaryUserIds(preservedUserIdsByRole);
      handleModalClose();
      globalThis.dispatchEvent(new Event("refresh-client-profile"));
      clearUnsavedChanges();
      dispatch({
        type: "UPDATE_SECTION",
        payload: { section: "isPrivate", value: false },
      });
    } catch (error) {
      console.error("Failed to make profile public:", error);
    } finally {
      setPrivacyLoading(false);
    }
  };

  return (
    <>
      <Box display={"flex"} alignItems={"center"} justifyContent={"space-between"} mb={2}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => attemptNavigation(sectionHeader.backTo)} size="small" sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant={"h3"} sx={{ mt: 0.5 }}>
            {/* A nested section names itself here, so it doesn't need a second header and
                back arrow of its own. */}
            {sectionHeader.title
              || (mode === "create" ? "Create New Client" : <TruncatedText text={safeClientName} maxLength={50} />)}
          </Typography>
        </Box>
        {canShowOption ? (
          <Box display={"flex"} gap={2}>
            {/* An inactive profile is read-only everywhere else (access is forced to
                "view" above), so privacy cannot be toggled on it either. */}
            {isTenantAdmin && !isInactive ? (
              <Button variant="outlined" color="primary" onClick={() => attemptProtectedAction(handleModelOpen)} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LockOutlined /> Private {isPrivateProfile ? <ToggleOnOutlined /> : <ToggleOffOutlined color="disabled" />}
              </Button>
            ) : null}
            <Button variant="outlined" color="primary" onClick={navigateTrackChanges}>
              Track Changes
            </Button>
            {isTenantAdmin ? (
              <Button variant="outlined" color="primary" onClick={() => attemptProtectedAction(handleOpenConfirm)}>
                {isInactive ? "Re Activate" : "Deactivate"}
              </Button>
            ) : null}
            <Button variant="contained" color="primary" onClick={() => downloadClientDocx()}>
              Download Docx
            </Button>
          </Box>
        ) : null}

        <Dialog open={openConfirm} onClose={handleCloseConfirm}>
          <DialogContent>
            <DialogContentText>{isInactive ? `Do you want to reactivate ${clientDetails?.clientName}?` : `Do you want to deactivate ${clientDetails?.clientName}?`}</DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseConfirm}>Cancel</Button>
            <Button onClick={handleConfirmAction} variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          sx={{
            opacity: 1,
            mx: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box bgcolor={"#fff"} width={500} borderRadius={2} p={4} maxHeight={630} overflow={"auto"}>
            {modalMode === "confirm-public" ? (
              <>
                <Typography mb={4}>Are you sure you want to change this client to public view? Added groups for private access will be removed.</Typography>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="text" onClick={handleModalClose} disabled={privacyLoading}>
                    No
                  </Button>

                  <Button variant="contained" onClick={handleMakePublic} disabled={privacyLoading}>
                    Yes
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <Typography variant="h3" mb={2}>
                  Assign access for Private Profiles
                </Typography>
                <Typography mb={2}>Turning on Private restricts profile access to the users assigned below.</Typography>

                <Box>
                  {assignmentRoles.map((role) => (
                    <Box key={role}>
                      <Box mb={2}>
                        <Typography>{role}</Typography>

                        <Box display="flex" gap={2} mt={1}>
                          <TextField label="Select Name" size="small" fullWidth select value={selectedValues[role] || ""} onChange={(e) => handleSelectChange(role, e.target.value)}>
                            {users
                              .filter((u) => isUserEligibleForRole(u.personid, role))
                              .map((user) => (
                                <MenuItem key={user.personid} value={user.personid}>
                                  {user.first_name} {user.last_name}
                                </MenuItem>
                              ))}
                          </TextField>

                          <Button variant="outlined" onClick={() => handleAdd(role)}>
                            Add
                          </Button>
                        </Box>

                        <Box mt={2} display="flex" gap={1} flexWrap="wrap">
                          {(roleData[role] || []).length > 0 && (
                            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block" }}>
                              Click on a name to Select/Unselect them as Primary.
                            </Typography>
                          )}
                          {(roleData[role] || []).map((user) => {
                            const isPrimary = primaryUserIds[role] === user.personid;
                            return <Chip key={user.personid} label={`${user.first_name} ${user.last_name}${isPrimary ? " - Primary" : ""}`} onDelete={() => handleDelete(role, user.personid)} color={isPrimary ? "primary" : "default"} onClick={() => handleSetPrimary(role, user.personid)} sx={{ cursor: "pointer", transition: "all 0.2s" }} />;
                          })}
                        </Box>
                      </Box>

                      <Divider sx={{ my: 3, borderWidth: 1, borderColor: "gray" }} />
                    </Box>
                  ))}
                </Box>

                <Box display="flex" justifyContent="flex-end" gap={2}>
                  <Button variant="text" onClick={handleModalClose} disabled={privacyLoading}>
                    Cancel
                  </Button>

                  <Button variant="contained" onClick={handlePrivate} disabled={privacyLoading}>
                    Save
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Modal>
      </Box>
      <Dialog open={showLeaveDialog} onClose={cancelLeave}>
        <DialogContent>
          <DialogContentText>Leave this page? You have unsaved changes. If you leave, your changes may not be saved.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelLeave}>Stay</Button>
          <Button onClick={confirmLeave} variant="contained">
            Leave
          </Button>
        </DialogActions>
      </Dialog>

      <Box data-kot-dirty-root="true">{renderStep()}</Box>
    </>
  );
};

export default AddClient;
